import { SIcon } from "@superwall-me/sui"
import { Page } from "integrations/contentful"
import { RichText } from "./RichText"
import { ColorBlur } from "app/splash/components/ColorBlur"
import { z } from "zod"
import { useRouter } from "next/router"
import LabeledTextField from "app/core/components/form/LabeledTextField"
import Form, { FORM_ERROR } from "app/core/components/form/Form"
import Link from "next/link"
import { DropdownSelectField } from "app/core/components/form/LabeledDropdownSelect"
import { useIntercom } from "react-use-intercom"
import useUtmParams from "app/core/hooks/useUTMParams"
import { useEffect, useRef, useState } from "react"
import { useScrollAnimation } from "app/splash/components/splash-sections/Customers"
import useAnalytics from "app/analytics/hooks/useAnalytics"
import { TAnalyticsEvent } from "@superwall/legacy-api-utils"
import React from "react"
import tw from "tailwind-styled-components"
import { LabeledDropdownV2 } from "app/core/components/form/LabeledDropdownV2"
import { LabeledTextFieldV2 } from "app/core/components/form/LabeledTextFieldV2"
const includedFeatures = [
  "Drag 'n Drop Paywalls",
  "200+ Custom Templates",
  "Unlimited A/B tests",
  "Surveys, Charts & More",
]

type DemoFormEstimatedRevenue = "0-50k" | "50k-100k" | "100k-500k" | "500k-1m" | "1m-5m" | "5m+"

type DemoFormValues = {
  firstName: string
  lastName?: string
  email: string
  website?: string
  company?: string
  estimatedRevenue?: DemoFormEstimatedRevenue
  phone?: string
}

type DemoFormProps = {
  onSuccess?: (values: DemoFormValues) => void
}

export const DemoFormSchema = z.object({
  firstName: z.string(),
  lastName: z.string().optional(),
  email: z.string().email(),
  company: z.string().optional(),
  estimatedRevenue: z.enum(["0-50k", "50k-100k", "100k-500k", "500k-1m", "1m-5m", "5m+"]),
  meetingType: z.enum(["sales", "technical"]),
})

export const BookDemoForm = ({ onSuccess }: DemoFormProps) => {
  // const [signupMutation] = useMutation(signup)
  const intercom = useIntercom()
  const router = useRouter()
  const { utmParams } = useUtmParams()

  const ref = useRef<HTMLInputElement>(null)

  let [formSubmitted, setFormSubmitted] = useState<boolean>(false)

  let track = useAnalytics()

  useEffect(() => {
    if (typeof window !== "undefined" && utmParams) {
      const properties = {
        utmCampaign: utmParams.utm_campaign,
        utmContent: utmParams.utm_content,
        utmMedium: utmParams.utm_medium,
        utmSource: utmParams.utm_source,
        utmTerm: utmParams.utm_term,
        ref: utmParams.ref,
        path: window.location.pathname,
      }
      console.log("[!] DemoRequest_Visit", utmParams)
      track({
        name: "DemoRequest_Visit",
        properties,
        group: {
          kind: "none",
          object: {},
        },
      } as TAnalyticsEvent)
    }
  }, [utmParams])

  if (formSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center  gap-10 my-10">
        <div className="border border-brand-primary/10 p-5 rounded-full bg-brand-primary/7">
          <SIcon type="check" className="text-brand-primary" size={32} />
        </div>
        <p className="text-white text-center max-w-[350px] text-lg">
          Thank you for your interest in Superwall. We will be in touch shortly.
        </p>
      </div>
    )
  }

  return (
    <div className="p-1">
      <Form
        submitText="Schedule Meeting →"
        schema={DemoFormSchema}
        validateOnBlur={false}
        initialValues={{
          firstName: ((router.query.name as string) || "").split(" ")[0] || undefined,
          lastName: ((router.query.name as string) || "").split(" ")[1] || "",
          email: (router.query.email as string) || undefined,
          company: (router.query.company as string) || undefined,
          meetingType: (router.query.meetingType as "sales" | "technical") || undefined,
        }}
        buttonFill={true}
        buttonProps={{
          variant: "juicy",
          className: "w-full rounded-full h-[45px] text-lg",
        }}
        onSubmit={async (values) => {
          try {
            const { firstName, lastName, ...credentials } = values
            const user = {
              ...credentials,
              name: [firstName, lastName].filter(Boolean).join(" "),
            }
            intercom?.boot({
              name: user.name,
              email: user.email,
              customAttributes: {
                web_estimated_revenue: user.estimatedRevenue,
                web_requested_demo: true,
                web_requested_demo_url: window.location.href,
                web_ref: utmParams?.ref,
                web_meeting_type: user.meetingType,
              },
              utmCampaign: utmParams?.utm_campaign,
              utmContent: utmParams?.utm_content,
              utmMedium: utmParams?.utm_medium,
              utmSource: utmParams?.utm_source,
              utmTerm: utmParams?.utm_term,
            })
            intercom?.trackEvent("requested-demo", {
              web_estimated_revenue: user.estimatedRevenue,
              web_requested_demo_url: window.location.href,
            })
            const properties = {
              utmCampaign: utmParams?.utm_campaign,
              utmContent: utmParams?.utm_content,
              utmMedium: utmParams?.utm_medium,
              utmSource: utmParams?.utm_source,
              utmTerm: utmParams?.utm_term,
              path: window.location.pathname,
              ref: utmParams?.ref,
              ...user,
            }
            console.log("[!] track DemoRequest_Complete", properties)
            track({
              name: "DemoRequest_Complete",
              properties,
              group: {
                kind: "none",
                object: {},
              },
            } as TAnalyticsEvent)
            onSuccess?.(values)
            console.log("[!] form complete!")

            const name = encodeURIComponent(values.firstName + " " + values.lastName)
            const email = encodeURIComponent(values.email)
            const company = encodeURIComponent(values.company || "")

            const a2 = encodeURIComponent(
              `-${values.meetingType === "sales" ? "Sales Demo" : "Technical Help"}\n-${
                values.estimatedRevenue
              }`
            )

            if (
              user.estimatedRevenue &&
              user.estimatedRevenue != "0-50k" &&
              user.estimatedRevenue != "50k-100k"
            ) {
              const url = `https://calendly.com/superwall/sales/?name=${name}&email=${email}&a1=${company}&a2=${a2}`
              window.open(url, "_blank")
            } else if (
              values.estimatedRevenue === "0-50k" ||
              values.estimatedRevenue === "50k-100k"
            ) {
              const url = `https://calendly.com/superwall/office-hours/?name=${name}&email=${email}&a1=${company}&a2=${a2}`
              window.open(url, "_blank")
            }
            setFormSubmitted(true)
          } catch (error) {
            console.log("[!] form error", error)
            return { [FORM_ERROR]: error.toString() }
          }
        }}
      >
        <div className="flex flex-col gap-3 mb-2 pb-2">
          <div className="w-full grid grid-cols-2 gap-3">
            <LabeledTextFieldV2
              className="w-full"
              name="firstName"
              label="First Name"
              placeholder="Jared"
            />
            <LabeledTextFieldV2
              className="w-full"
              name="lastName"
              label="Last Name"
              placeholder="Dunn"
            />
          </div>
          <LabeledTextFieldV2
            ref={ref}
            name="email"
            label="Email"
            placeholder="email@company.com"
          />
          <LabeledTextFieldV2 name="company" label="Company" placeholder="Company Inc." />
          <div className="w-full grid grid-cols-2 gap-3">
            <LabeledDropdownV2
              name="estimatedRevenue"
              label="Monthly Revenue"
              overrideWrapperClassName="relative"
              options={[
                { label: "Less than $50K", value: "0-50k" },
                { label: "$50K - $100K", value: "50k-100k" },
                { label: "$100K - $500K", value: "100k-500k" },
                { label: "$500K - $1M", value: "500k-1m" },
                { label: "$1M - $5M", value: "1m-5m" },
                { label: "More than $5M", value: "5m+" },
              ]}
            />
            <LabeledDropdownV2
              name="meetingType"
              label="Meeting Type"
              overrideWrapperClassName="relative"
              options={[
                { label: "Speak to Sales", value: "sales" },
                { label: "Technical Help", value: "technical" },
              ]}
            />
          </div>
        </div>
      </Form>
    </div>
  )
}

const Title = tw.h1`
sm:mt-0
text-4xl
sm:text-4xl
md:text-5xl
xl:text-6xl
2xl:text-7xl
font-medium
md:!leading-tight
bg-clip-text
text-transparent bg-gradient-to-br from-white to-grayscale-lightest
max-w-[500px]
md:max-w-[800px]
lg:max-w-[1000px]"
`

const Subtitle = tw.h2`
text-white
text-opacity-50
text-xl
md:text-2xl
max-w-[570px]
leading-normal
px-2
md:px-0
        max-w-5xl !text-opacity-50
`

export const ContactForm = ({ page }: { page: Page }) => {
  let [formSubmitted, setFormSubmitted] = useState<boolean>(false)

  return (
    <div className="md:mt-5 md:my-10 2xl:my-14">
      <ColorBlur opacity={0.05} />
      <div className="mx-auto mb-7 md:mb-10 2xl:mb-14 2xl:py-2 flex flex-col items-center justify-center text-center max-w-5xl">
        <Title>{page.title}</Title>
        <Subtitle>{page.subtitle}</Subtitle>
      </div>
      <div
        style={{
          borderRadius: "calc(min(max(16px, 2vw), 40px))",
        }}
        className="bg-grayscale-lighter bg-opacity-10 border border-white border-1 border-opacity-10 !p-3 !w-full !max-w-6xl mx-auto"
      >
        <div className="w-full flex flex-col-reverse gap-7 md:gap-0 md:flex-row">
          <div className="w-full px-2 py-2 md:py-5 md:px-7 mr-5 text-left flex flex-col items-start justify-between">
            <div className="flex-grow">
              {React.isValidElement(page.body) ? (
                <div className="prose">{page.body}</div>
              ) : (
                <RichText document={page.body} />
              )}
            </div>
            <div className="w-full flex gap-7 items-center justify-center">
              <p className="text-brand-primary py-5 font-bold text-lg whitespace-nowrap">{`Key features`}</p>
              <div className="h-[1px] w-full bg-white opacity-7"></div>
            </div>
            <ul
              role="list"
              className="grid grid-cols-1 gap-2 md:gap-4 leading-6 sm:grid-cols-2 sm:gap-6 pb-5"
            >
              {includedFeatures.map((feature) => (
                <li key={feature} className="grid grid-cols-[auto,1fr] items-baseline gap-x-3">
                  <SIcon type="check" className="text-brand-primary" size={16} />
                  <span className="lg:text-xl lg:tracking-tight ">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            style={{
              borderRadius: "calc(min(max(4px, 1.4vw), 28px))",
            }}
            className="bg-white flex items-center justify-center md:max-w-sm lg:max-w-md w-full bg-opacity-[0.02] border border-white border-opacity-7 shadow-xl "
          >
            <div className="mx-auto w-full p-5 gap-4">
              <BookDemoForm
                onSuccess={() => {
                  setFormSubmitted(true)
                }}
              />
              {!formSubmitted && (
                <p className="text-xs text-white/40 px-2 mt-4 text-center">
                  By proceeding you consent to receiving emails and our{" "}
                  <Link href="/privacy" target="_blank" className="underline">
                    terms
                  </Link>
                  .
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
